import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActionsAnalyticsService, CART_TYPES, CustomizableMobilePlan, IPlanAddOn, MobileCustomPlansServiceNoSync, ShippingConfigurationService, UserAccountServiceNoSync, UserPlansService } from '@ztarmobile/zwp-service-backend';
import { takeWhile } from 'rxjs/operators';
import { ACCOUNT_ROUTE_URLS, PLANS_SHOP_ROUTE_URLS, ROUTE_URLS, SHOP_ROUTE_URLS } from 'src/app/app.routes.names';
import { AppState } from 'src/app/app.service';
import { ModalHelper } from 'src/services/modal-helper.service';
import { ToastrHelperService } from 'src/services/toast-helper.service';
import { PhonePipe } from 'src/widgets/pipes/phone.pipe';
import { CheckoutService } from '../checkout/checkout.service';
import { NUMBERS_ONLY_PATTERN } from '@app/app.config';
import { TaxesService } from '@ztarmobile/zwp-service-backend-v2';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit, OnDestroy {
  public items = true;
  public lines = true;
  public taxes = true;
  public zipCode: string;
  public processingRequest = false;
  public validZipCode = false;
  public isEstimatedTaxesAndFees = false;
  public autoRenew: boolean;
  public userCart: CustomizableMobilePlan;
  public CART_TYPES = CART_TYPES;
  public purchasedMdn: string;
  public mappedPurchasedMdn: string;
  public estimatedTaxes;
  public estimatedFees;
  public isApplicablePromo = true;
  public isTopupChecked = false;
  public newSimOrder: { price: number; fees: number; id?: string };
  public internationalAddon: IPlanAddOn = null;
  public dataAddons: Array<IPlanAddOn> = [];
  public payGoAddons: Array<IPlanAddOn> = [];
  public zipCodeTouched = false;
  public numberPattern = NUMBERS_ONLY_PATTERN;

  private alive = true;
  checked: boolean;

  constructor(
    private router: Router,
    private appState: AppState,
    private mobilePlansService: MobileCustomPlansServiceNoSync,
    private toastHelper: ToastrHelperService,
    private modalHelper: ModalHelper,
    private analyticsService: ActionsAnalyticsService,
    private userAccountService: UserAccountServiceNoSync,
    private userPlanService: UserPlansService,
    private shippingConfigurationService: ShippingConfigurationService,
    private checkoutService: CheckoutService,
    private taxesService: TaxesService
  ) {
    this.shippingConfigurationService.newSimOrder.pipe(takeWhile(() => this.alive)).subscribe((order) => {
      this.newSimOrder = order;
    });
  }

  ngOnInit(): void {
    this.appState.loading = true;
    this.mobilePlansService.currentPlan.pipe(takeWhile(() => this.alive)).subscribe((plan) => {
      this.userCart = plan;
      setTimeout(() => {
        if (!!this.userCart && !!this.userCart?.cartType) {
          this.autoRenew = this.userCart?.autoRenewPlan;
          if (this.userCart.cartType === CART_TYPES.NEW_PLAN) {
            sessionStorage.removeItem('shippingAddress');
            sessionStorage.removeItem('shippingMethod');
            sessionStorage.removeItem('payment_id');
          }
          if (!!this.userCart && this.userCart?.cartType === CART_TYPES.TOPUP_PLAN && !this.isTopupChecked || this.userCart.cartType === CART_TYPES.CHANGE_PLAN || this.userCart.cartType === CART_TYPES.PLAN_ITEMS) {
            const activeUserPlanId = !!this.userCart.activePlanId ? this.userCart.activePlanId : sessionStorage.getItem('plan_id');
            this.userPlanService.getUserPlan(activeUserPlanId).then((plan) => {
              if (!!plan) {
                if (this.userCart.cartType !== CART_TYPES.PLAN_ITEMS) {
                  this.autoRenew = this.userCart.autoRenewPlan;
                } else {
                  this.mobilePlansService.setAutoRenewPlan(false);
                }
                this.mappedPurchasedMdn = (new PhonePipe()).transform(plan?.mdn);
                this.purchasedMdn = plan?.mdn;
                if (this.userCart.cartType === CART_TYPES.CHANGE_PLAN && this.userCart.basePlan.subscriptionCycles === 3 && !this.checked) {
                  this.checked = true;
                  this.appState.loading = true;
                  this.userAccountService.checkApplicablePromotion(this.purchasedMdn, this.userCart.basePlan.id).then((result) => {
                    this.isApplicablePromo = result.applicable;
                    this.appState.loading = false;
                  }, (error) => {
                    this.toastHelper.showAlert(error.message);
                    this.appState.loading = false;
                  });
                }
              }
            });
            this.isTopupChecked = true;
            if (!!this.userCart?.addOns) {
              this.filterAddons(this.userCart?.addOns);
            }
          }
        } else {
          this.router.navigate([ROUTE_URLS.HOME]);
        }
      }, 1000);
      this.appState.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  public calculateTotal(): number {
    let total = 0;
    if (!this.userCart) {
      return 0;
    }
    if (this.userCart.cartType === CART_TYPES.NEW_PLAN) {
      if (!!this.userCart) {
        if (!!this.userCart.basePlan.subscriptionCycles) {
          total += this.userCart.basePlan.promoMoPrice * this.userCart.basePlan.subscriptionCycles;
        } else {
          total += this.userCart.price;
        }
      }
      if (!!this.estimatedTaxes) {
        total += this.estimatedTaxes;
      }
      if (!!this.estimatedFees) {
        total += this.estimatedFees;
      }
    }
    if (this.userCart.cartType === CART_TYPES.CHANGE_PLAN) {
      if (!!this.userCart) {
        if (!!this.userCart.basePlan.subscriptionCycles) {
          total += !!this.isApplicablePromo ? this.userCart.basePlan.promoMoPrice * this.userCart.basePlan.subscriptionCycles :
            this.userCart.basePlan.price * this.userCart.basePlan.subscriptionCycles;
        } else {
          total += this.userCart.price;
        }
      }
      if (!!this.estimatedTaxes) {
        total += this.estimatedTaxes;
      }
      if (!!this.estimatedFees) {
        total += this.estimatedFees;
      }
    }
    if (this.userCart.cartType === CART_TYPES.TOPUP_PLAN) {
      if (!!this.userCart) {
        if (!!this.userCart?.basePlan?.subscriptionCycles && this.userCart?.basePlan?.price) {
          total += this.userCart?.basePlan?.price * this.userCart?.basePlan?.subscriptionCycles;
        } else {
          total += this.userCart?.basePlan?.price;
        }
      }
      if (!!this.estimatedTaxes) {
        total += this.estimatedTaxes;
      }
      if (!!this.estimatedFees) {
        total += this.estimatedFees;
      }
    }
    if (!!this.userCart.addOns) {
      for (const addon of this.userCart.addOns) {
        total += addon.price * addon.quantity;
      }
      if (!!this.estimatedTaxes) {
        total += this.estimatedTaxes;
      }
      if (!!this.estimatedFees) {
        total += this.estimatedFees;
      }
    }
    if (this.userCart.simsQuantity > 0 && this.userCart.cartType !== CART_TYPES.NEW_PLAN) {
      if (!!this.newSimOrder && this.userCart.cartType === CART_TYPES.PLAN_ITEMS) { // this condition to disable sim price calculation for migration)
        total = total + this.newSimOrder.price;
      }
      if (!!this.estimatedTaxes && !this.userCart.addOns) {
        total += this.estimatedTaxes;
      }
      if (!!this.estimatedFees && !this.userCart.addOns) {
        total += this.estimatedFees;
      }
    }
    return total >= 0 ? total : 0;
  }

  public updateAutoRenew(): void {
    this.checkoutService.updateAutoRenew(this.autoRenew);
    this.mobilePlansService.setAutoRenewPlan(this.autoRenew);
  }

  public goToPlans(): void {
    this.modalHelper.showConfirmMessageModal('Are you sure you want to change plan?', 'By clicking yes you agree to leaving this page and changing your FreeUp plan', 'Yes', 'Cancel',
      'confirm-change-checkout-modal').afterClosed().subscribe((result) => {
        if (result) {
          sessionStorage.removeItem('payment_id');
          sessionStorage.removeItem('useFromBalance');
          if (this.userCart.cartType === CART_TYPES.CHANGE_PLAN) {
            this.router.navigate([`${SHOP_ROUTE_URLS.BASE}/${SHOP_ROUTE_URLS.PLANS_AND_FEATURES}/${PLANS_SHOP_ROUTE_URLS.CHANGE_PLAN}`]);
          } else {
            this.router.navigate([`${SHOP_ROUTE_URLS.BASE}/${SHOP_ROUTE_URLS.PLANS_AND_FEATURES}/${PLANS_SHOP_ROUTE_URLS.NEW_PLAN}`]);
          }
        }
      });
  }

  public deleteCart(event, allCart?): void {
    if (!this.userCart) {
      return;
    }
    let question;
    let confirmMessage;
    if (this.userCart.cartType === CART_TYPES.NEW_PLAN) {
      allCart = true;
    }
    if (!!allCart) {
      question = `Are you sure you want to empty all the items in your shopping cart?`;
      confirmMessage = `This removes ALL items from your shopping cart. Click 'No' to keep items and proceed with checkout.`;
    } else {
      question = (this.userCart.cartType === CART_TYPES.CHANGE_PLAN) ?
        'Are you sure you want to remove plan?' : 'Are you sure you want to remove item?';
      confirmMessage = (this.userCart.cartType === CART_TYPES.NEW_PLAN) || (this.userCart.cartType === CART_TYPES.CHANGE_PLAN) ?
        'By clicking yes you agree to remove plan from your cart' : 'By clicking yes you agree to remove item from your cart';
    }
    this.modalHelper.showConfirmMessageModal(question, confirmMessage, 'Yes', 'No', 'clear-cart-modal')
      .afterClosed().subscribe((result) => {
        if (!!result) {
          switch (this.userCart.cartType) {
            case CART_TYPES.NEW_PLAN:
              this.mobilePlansService.clearUserCart();
              this.appState.clearSessionStorage();
              sessionStorage.setItem('removeFromCart', 'true');
              this.checkoutService.paymentsSubject.next(null);
              this.checkoutService.detailsSubject.next(null);
              this.analyticsService.trackRermoveFromCartGA4([this.userCart.basePlan]);
              this.router.navigate([ROUTE_URLS.HOME]);
              break;
            case CART_TYPES.PLAN_ITEMS:
              this.removeItemFromCart(event, allCart);
              break;
            case CART_TYPES.CHANGE_PLAN:
              this.mobilePlansService.clearUserCart();
              this.checkoutService.paymentsSubject.next(null);
              this.checkoutService.detailsSubject.next(null);
              this.analyticsService.trackRermoveFromCartGA4([this.userCart.basePlan]);
              this.appState.clearSessionStorage();
              sessionStorage.setItem('removeFromCart', 'true');
              this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.SUMMARY}`]);
              break;
            case CART_TYPES.TOPUP_PLAN:
              this.checkoutService.paymentsSubject.next(null);
              this.checkoutService.detailsSubject.next(null);
              this.mobilePlansService.clearUserCart();
              this.appState.clearSessionStorage();
              this.analyticsService.trackRermoveFromCartGA4([this.userCart.basePlan]);
              this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.SUMMARY}`]);
              break;
            default:
              this.checkoutService.paymentsSubject.next(null);
              this.checkoutService.detailsSubject.next(null);
              this.analyticsService.trackRermoveFromCartGA4([this.userCart.basePlan]);
              this.mobilePlansService.clearUserCart();
              this.appState.clearSessionStorage();
              this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.SUMMARY}`]);
              break;
          }
        }
      });
  }

  public validateZipCode(): any {
    this.validZipCode = /^\d{5}(-\d{4})?$/.test(this.zipCode);
    return this.validZipCode;
  }

  public estimateTaxesAndFees(): void {
    if (!!this.zipCode && this.validZipCode) {
      this.appState.loading = true;
      this.taxesService.calculateEstimateTaxes(this.zipCode, !!this.userCart?.autoRenewPlan?this.userCart?.autoRenewPlan: false ).then((result) => {
        this.applyEstimatedTaxes(result.data);
       }, (error) => {
         this.applyResetEstimatedTaxes(error);
       })
    }
  }

  public editZipCode(): void {
    this.isEstimatedTaxesAndFees = false;
    this.zipCode = '';
  }

  public goToAddons(): void {
    const params = {};
    params[ACCOUNT_ROUTE_URLS.PARAMS.CHANGE_ADDON] = true;
    this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.PLAN_ADD_ONS}`, params]);
  }

  public cancel(): void {
    this.router.navigate([ROUTE_URLS.HOME]);
  }

  public goToCheckout(): void {
    this.router.navigate([`${SHOP_ROUTE_URLS.BASE}/${SHOP_ROUTE_URLS.CHECKOUT}`]);
  }

  private filterAddons(planAddons): void {
    if (!!planAddons && planAddons?.length > 0) {
      this.dataAddons = planAddons?.filter((addon) => addon.type === 'add-on-data');
      this.internationalAddon = planAddons?.filter((addon) => addon.type === 'international-talk');
      this.payGoAddons = planAddons?.filter((addon) => addon.type === 'add-on-talkandtext');
    } else {
      this.dataAddons = [];
      this.internationalAddon = null;
      this.payGoAddons = [];
    }
  }

  private removeItemFromCart(item: string, allCart?: boolean): void {
    const cartHasMultipleItems = this.userCart.simsQuantity > 0 && !!this.userCart?.addOns && this.userCart?.addOns?.length > 0;
    if (!allCart) {
      if (item === 'sim') {
        if (!!this.userCart.addOns && this.userCart.addOns.length > 0) {
          this.userCart.simsQuantity = 0;
          this.mobilePlansService.setSimPurchaseQuantity(0);
        } else {
          this.mobilePlansService.clearUserCart();
          this.appState.clearSessionStorage();
          this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.MANAGE_DEVICES}`]);
        }
      }
      if (item === 'plan') {
        this.mobilePlansService.clearUserCart();
        this.appState.clearSessionStorage();
        this.analyticsService.trackRermoveFromCart([this.userCart.basePlan]);
        this.analyticsService.trackRermoveFromCartGA4([this.userCart.basePlan]);
        this.router.navigate([ROUTE_URLS.HOME]);
      }
      if (item !== 'sim' && item !== 'plan') { // remove from addons
        if (!!this.userCart.addOns && this.userCart.addOns.length === 1 && !!cartHasMultipleItems) { // cart has sim and one addon
          this.filterAddons([]);
        }
        if (!!this.userCart.addOns && this.userCart.addOns?.length === 1 && !cartHasMultipleItems) { // if cart has only one addon then clear cart
          this.mobilePlansService.clearUserCart();
          this.appState.clearSessionStorage();
          this.router.navigate([`${ACCOUNT_ROUTE_URLS.BASE}/${ACCOUNT_ROUTE_URLS.PLAN_ADD_ONS}`]);
        } else {
          let cartAddons = this.userCart?.addOns;
          cartAddons = cartAddons.filter((addon) => addon.id !== item);
          this.filterAddons(cartAddons);
          this.mobilePlansService.setAddonsList(cartAddons, this.userCart).then(() => {
          });
        }
        this.analyticsService.trackRermoveFromCart(this.userCart.addOns);
        this.analyticsService.trackRermoveFromCartGA4(this.userCart.addOns);
      }
    } else {
      this.mobilePlansService.clearUserCart();
      this.mobilePlansService.setSimPurchaseQuantity(0);
      this.checkoutService.paymentsSubject.next(null);
      this.checkoutService.detailsSubject.next(null);
      if (!!this.userCart.basePlan && !!this.userCart.basePlan.parentId) {
        this.analyticsService.trackRermoveFromCart([this.userCart.basePlan]);
        this.analyticsService.trackRermoveFromCartGA4([this.userCart.basePlan]);
      }
      if (!!this.userCart.addOns && this.userCart.addOns.length > 0) {
        this.analyticsService.trackRermoveFromCart(this.userCart.addOns);
        this.analyticsService.trackRermoveFromCartGA4(this.userCart.addOns);
      }
      this.router.navigate([ROUTE_URLS.HOME]);
    }
  }

  private applyEstimatedTaxes(result): void {
    this.estimatedTaxes = result.taxes;
    this.estimatedFees = result.fees;
    this.appState.loading = false;
    this.isEstimatedTaxesAndFees = true;
    this.zipCodeTouched = false;
  }

  private applyResetEstimatedTaxes(error): void {
    this.appState.loading = false;
    this.toastHelper.showAlert(error?.error?.errors[0]?.message);
    this.estimatedTaxes = 0;
    this.estimatedFees = 0;
    this.zipCode = '';
    this.isEstimatedTaxesAndFees = false;
    this.zipCodeTouched = false;
  }

}
